/** Heroicons-style glyphs used by dashboard navigation and related marks. */
export type DashboardNavIconName =
  | 'dashboard'
  | 'statements'
  | 'donations'
  | 'claim'
  | 'matching'
  | 'matching-inactive'
  | 'settings'
  | 'support'
  | 'logout'
  | 'menu'
  | 'email'
  | 'card'
  | 'history'

export type DashboardNavIconPath = {
  d: string
  fillRule?: 'evenodd'
  clipRule?: 'evenodd'
}

export type DashboardNavIconGlyph = {
  viewBox: string
  variant: 'solid' | 'outline'
  paths: DashboardNavIconPath[]
}

function solidIcon(paths: DashboardNavIconPath[]): DashboardNavIconGlyph {
  return { viewBox: '0 0 20 20', variant: 'solid', paths }
}

function outlineIcon(paths: DashboardNavIconPath[]): DashboardNavIconGlyph {
  return { viewBox: '0 0 24 24', variant: 'outline', paths }
}

const DASHBOARD_NAV_ICONS: Record<
  DashboardNavIconName,
  DashboardNavIconGlyph
> = {
  dashboard: solidIcon([
    {
      d: 'M10.707 2.293a1 1 0 00-1.414 0l-7 7a1 1 0 001.414 1.414L4 10.414V17a1 1 0 001 1h2a1 1 0 001-1v-2a1 1 0 011-1h2a1 1 0 011 1v2a1 1 0 001 1h2a1 1 0 001-1v-6.586l.293.293a1 1 0 001.414-1.414l-7-7z',
    },
  ]),
  statements: solidIcon([
    {
      d: 'M4 4a2 2 0 012-2h4.586A2 2 0 0112 2.586L15.414 6A2 2 0 0116 7.414V16a2 2 0 01-2 2H6a2 2 0 01-2-2V4zm2 6a1 1 0 011-1h6a1 1 0 110 2H7a1 1 0 01-1-1zm1 3a1 1 0 100 2h6a1 1 0 100-2H7z',
      fillRule: 'evenodd',
      clipRule: 'evenodd',
    },
  ]),
  donations: solidIcon([
    {
      d: 'M3.172 5.172a4 4 0 015.656 0L10 6.343l1.172-1.171a4 4 0 115.656 5.656L10 17.657l-6.828-6.829a4 4 0 010-5.656z',
      fillRule: 'evenodd',
      clipRule: 'evenodd',
    },
  ]),
  // Heroicons 20 solid `currency-pound`: filled disc with a £ knockout.
  // Same glyph in the sidebar and the home notification.
  claim: solidIcon([
    {
      d: 'M10 18a8 8 0 1 0 0-16 8 8 0 0 0 0 16ZM8.732 6.232a2.5 2.5 0 0 1 3.536 0 .75.75 0 1 0 1.06-1.06A4 4 0 0 0 6.5 8v.165c0 .364.034.728.1 1.085h-.35a.75.75 0 0 0 0 1.5h.737a5.25 5.25 0 0 1-.367 3.072l-.055.123a.75.75 0 0 0 .848 1.037l1.272-.283a3.493 3.493 0 0 1 1.604.021 4.992 4.992 0 0 0 2.422 0l.97-.242a.75.75 0 0 0-.363-1.456l-.971.243a3.491 3.491 0 0 1-1.694 0 4.992 4.992 0 0 0-2.258-.038c.19-.811.227-1.651.111-2.477H9.75a.75.75 0 0 0 0-1.5H8.136A4.397 4.397 0 0 1 8 8.165V8c0-.641.244-1.28.732-1.768Z',
      fillRule: 'evenodd',
      clipRule: 'evenodd',
    },
  ]),
  matching: solidIcon([
    {
      d: 'M2.166 4.999A11.954 11.954 0 0010 1.944 11.954 11.954 0 0017.834 5c.11.65.166 1.32.166 2.001 0 5.225-3.34 9.67-8 11.317C5.34 16.67 2 12.225 2 7c0-.682.057-1.35.166-2.001zm11.541 3.708a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z',
      fillRule: 'evenodd',
      clipRule: 'evenodd',
    },
  ]),
  'matching-inactive': solidIcon([
    {
      // Heroicons 20 solid `shield-exclamation`. The older path we had drew the
      // bang at x=10.5, so it sat right of centre in the shield.
      d: 'M10.339 2.237a.531.531 0 0 0-.678 0 11.947 11.947 0 0 1-7.078 2.75.5.5 0 0 0-.479.425A12.11 12.11 0 0 0 2 7c0 5.163 3.26 9.564 7.834 11.257a.48.48 0 0 0 .332 0C14.74 16.564 18 12.163 18 7c0-.538-.035-1.069-.104-1.589a.5.5 0 0 0-.48-.425 11.947 11.947 0 0 1-7.077-2.75ZM10 6a.75.75 0 0 1 .75.75v3.5a.75.75 0 0 1-1.5 0v-3.5A.75.75 0 0 1 10 6Zm0 9a1 1 0 1 0 0-2 1 1 0 0 0 0 2Z',
      fillRule: 'evenodd',
      clipRule: 'evenodd',
    },
  ]),
  settings: solidIcon([
    {
      d: 'M10 9a3 3 0 100-6 3 3 0 000 6zm-7 9a7 7 0 1114 0H3z',
      fillRule: 'evenodd',
      clipRule: 'evenodd',
    },
  ]),
  support: solidIcon([
    {
      d: 'M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-8-3a1 1 0 00-.867.5 1 1 0 11-1.731-1A3 3 0 0113 8a3.001 3.001 0 01-2 2.83V11a1 1 0 11-2 0v-1a1 1 0 011-1 1 1 0 100-2zm0 8a1 1 0 100-2 1 1 0 000 2z',
      fillRule: 'evenodd',
      clipRule: 'evenodd',
    },
  ]),
  // Heroicons 20 solid `arrow-right-on-rectangle` — door + arrow reads clearly at 16px.
  logout: solidIcon([
    {
      d: 'M3 4.25C3 3.00736 4.00736 2 5.25 2H10.75C11.9926 2 13 3.00736 13 4.25V6.25C13 6.66421 12.6642 7 12.25 7C11.8358 7 11.5 6.66421 11.5 6.25V4.25C11.5 3.83579 11.1642 3.5 10.75 3.5H5.25C4.83579 3.5 4.5 3.83579 4.5 4.25V15.75C4.5 16.1642 4.83579 16.5 5.25 16.5H10.75C11.1642 16.5 11.5 16.1642 11.5 15.75V13.75C11.5 13.3358 11.8358 13 12.25 13C12.6642 13 13 13.3358 13 13.75V15.75C13 16.9926 11.9926 18 10.75 18H5.25C4.00736 18 3 16.9926 3 15.75V4.25Z',
      fillRule: 'evenodd',
      clipRule: 'evenodd',
    },
    {
      d: 'M6 10C6 9.58579 6.33579 9.25 6.75 9.25H16.2955L15.2483 8.30747C14.9404 8.03038 14.9154 7.55616 15.1925 7.24828C15.4696 6.94039 15.9438 6.91543 16.2517 7.19253L18.7517 9.44253C18.9098 9.58476 19 9.78738 19 10C19 10.2126 18.9098 10.4152 18.7517 10.5575L16.2517 12.8075C15.9438 13.0846 15.4696 13.0596 15.1925 12.7517C14.9154 12.4438 14.9404 11.9696 15.2483 11.6925L16.2955 10.75H6.75C6.33579 10.75 6 10.4142 6 10Z',
      fillRule: 'evenodd',
      clipRule: 'evenodd',
    },
  ]),
  menu: solidIcon([
    {
      d: 'M3 4.75a.75.75 0 0 1 .75-.75h12.5a.75.75 0 0 1 0 1.5H3.75A.75.75 0 0 1 3 4.75Zm0 5.25a.75.75 0 0 1 .75-.75h12.5a.75.75 0 0 1 0 1.5H3.75A.75.75 0 0 1 3 10Zm0 5.25a.75.75 0 0 1 .75-.75h12.5a.75.75 0 0 1 0 1.5H3.75a.75.75 0 0 1-.75-.75Z',
      fillRule: 'evenodd',
      clipRule: 'evenodd',
    },
  ]),
  email: solidIcon([
    {
      d: 'M2.003 5.884L10 9.882l7.997-3.998A2 2 0 0016 4H4a2 2 0 00-1.997 1.884z',
    },
    { d: 'M18 8.118l-8 4-8-4V14a2 2 0 002 2h12a2 2 0 002-2V8.118z' },
  ]),
  card: solidIcon([
    { d: 'M4 4a2 2 0 00-2 2v1h16V6a2 2 0 00-2-2H4z' },
    {
      d: 'M18 9H2v5a2 2 0 002 2h12a2 2 0 002-2V9zM4 13a1 1 0 011-1h1a1 1 0 110 2H5a1 1 0 01-1-1zm5-1a1 1 0 100 2h6a1 1 0 100-2H9z',
      fillRule: 'evenodd',
      clipRule: 'evenodd',
    },
  ]),
  history: solidIcon([
    {
      d: 'M10 18a8 8 0 100-16 8 8 0 000 16zm1-12a1 1 0 10-2 0v4a1 1 0 00.293.707l2.828 2.829a1 1 0 101.415-1.415L11 9.586V6z',
      fillRule: 'evenodd',
      clipRule: 'evenodd',
    },
  ]),
}

export function getDashboardNavIcon(
  name: DashboardNavIconName
): DashboardNavIconGlyph {
  return DASHBOARD_NAV_ICONS[name]
}

export function getDashboardNavIconPaths(
  name: DashboardNavIconName
): DashboardNavIconPath[] {
  return DASHBOARD_NAV_ICONS[name].paths
}

/** Inline SVG markup for Driver.js popover titles and other non-Vue DOM. */
export function renderDashboardNavIconSvg(name: DashboardNavIconName): string {
  const glyph = getDashboardNavIcon(name)
  const paths = glyph.paths
    .map((path) => {
      const attrs = [
        `d="${path.d}"`,
        path.fillRule ? `fill-rule="${path.fillRule}"` : '',
        path.clipRule ? `clip-rule="${path.clipRule}"` : '',
      ]
        .filter(Boolean)
        .join(' ')
      return `<path ${attrs} />`
    })
    .join('')

  if (glyph.variant === 'outline') {
    return `<svg viewBox="${glyph.viewBox}" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">${paths}</svg>`
  }

  return `<svg viewBox="${glyph.viewBox}" fill="currentColor" aria-hidden="true">${paths}</svg>`
}
